/**
 * Every colour the map draws, in one place.
 *
 * Until now each layer declared its own constants — `TEAL` in incidents,
 * `AMBER` in streetwork, `RED` in police, `STYLE` maps in land use and
 * entrances. That was fine while there was one look. It makes a second look
 * impossible, because "change the theme" would mean editing twelve files and
 * hoping none were missed.
 *
 * Layers read `palette()` at render time rather than capturing a constant at
 * import time, so switching themes needs only a repaint.
 *
 * The `dedsec` entry is the existing look, moved across unchanged. Nothing
 * about the default appearance should shift as a result of this refactor.
 */

const STORAGE_KEY = "pulse:theme";

/**
 * The original: near-black ground, greyscale city, colour spent only on live
 * data. Every value here was lifted from the layer that used to own it.
 */
const dedsec = {
  name: "DedSec",
  /** Light basemaps need dark labels and vice versa; layers ask rather than assume. */
  light: false,

  // Off-black rather than black. At #050507 the ground read as an absence
  // rather than a surface, and nothing sitting on it had anywhere to sit.
  ground: "#101117",
  water: "#05070c",

  // The streets have to be lighter than the buildings, not darker.
  //
  // These ran #141416 to #4b4b52, every one of them below the building ramp.
  // Downtown that inverts the city: footprints fill the blocks, so the street
  // grid became thin near-black gaps between pale masses and disappeared
  // exactly where the streets matter most, while staying legible out in the
  // sparse neighbourhoods where nothing covers the ground. Mid-greys put the
  // corridors above the building bases and below the tower tops, which is
  // both how a lit street looks from above and what makes the grid readable.
  // Brighter than the buildings, not merely brighter than the ground.
  //
  // The first attempt at this put minor roads at #3a3b42, which clears the
  // ground comfortably and still vanished downtown: the height ramp puts a 30m
  // building at [54,57,68] and a 60m one at [67,70,80], so the streets landed
  // inside the same luminance band as the mid-rises that line them and the
  // grid dissolved into the blocks. What makes a grid read is that the
  // corridors are the brightest thing at street level, with only tower tops
  // above them.
  roadService: "#3e4048",
  roadMinor: "#5c5f6a",
  roadMedium: "#707380",
  roadMajor: "#848796",
  roadHighway: "#9c9fae",
  roadLabel: "#bec1cd",
  roadLabelHalo: "#000000",

  // Buildings are shaded by height between these two, and the low end has to
  // clear the ground it stands on.
  //
  // This ran 16 -> 60 against a #050507 ground: eleven units of contrast for a
  // short building, and even a 217m tower reaching only 60. Denver's extract is
  // 31,114 buildings under 8m out of 43,071 downtown, so very nearly the whole
  // city was painted within a few units of the background — the geometry was
  // there, extruded, lit, pickable, and looked exactly like empty ground. The
  // ramp was set before the ground was darkened and never rebalanced after.
  // Raised again with the ground: lifting the ground to #101117 without
  // lifting this floor would have put a short building back within fifteen
  // units of what it stands on, which is the fault this ramp exists to avoid.
  buildingLow: [42, 45, 55],
  buildingHigh: [132, 136, 148],
  /** Cold cast for buildings nothing is known about. */
  buildingUnknown: [0.88, 0.92, 1.08],
  buildingTintRed: [98, 22, 24],
  buildingTintBlue: [24, 42, 98],

  // Green you can actually see.
  //
  // These were faded and darkened on purpose, to stop parks shouting over the
  // live data. They went too far: park green was [7,16,10] at alpha 150 and
  // verges [11,14,12] at alpha 42, which against the old near-black ground was
  // nothing at all, and against the lifted ground is less than nothing. Parks
  // now read as parks and verges as a hint of one, while staying well below
  // the incident and camera colours in saturation.
  park: [18, 46, 26, 185],
  parkLine: [36, 82, 46, 160],
  greenery: [20, 38, 25, 105],
  greeneryLine: [30, 56, 36, 90],
  // Lifted with the ground too. Parking was [17,17,20] against a [5,5,7]
  // ground and is now standing on [16,17,23] — it would have been the same
  // colour as the surface it sits on.
  parking: [34, 34, 40, 175],
  parkingLine: [72, 72, 82, 140],
  vacant: [44, 37, 26, 180],
  vacantLine: [104, 82, 44, 165],
  construction: [30, 26, 12, 175],
  constructionLine: [116, 94, 28, 180],
  tree: [18, 40, 25, 120],

  incident: [46, 230, 192],
  incidentDim: [24, 140, 120],

  cameraOsm: [124, 112, 146],
  cameraHalo: [138, 132, 178],
  cameraUser: [168, 148, 200],
  alpr: [255, 72, 72],
  // Denver PD's own plate readers, distinct from the commercial Flock red so
  // the two networks do not read as one.
  alprPolice: [255, 120, 175],
  // A light purple, close to the event flags. Clear of both the ALPR red and
  // the roadwork/permit amber it kept being mistaken for.
  gunshot: [200, 140, 252],
  feed: [120, 220, 255],

  roadwork: [255, 176, 0],
  permit: [214, 150, 40],
  police: [255, 72, 72],
  event: [214, 150, 255],

  cotripIncident: [255, 108, 60],
  cotripCondition: [120, 170, 210],
  cotripClosed: [255, 40, 40],

  /** Entrance pictograms are greys; the number is the shade. */
  entranceDoor: 186,
  entranceGate: 172,
  entranceService: 160,
  entranceExit: 200,
  entranceInferred: [150, 158, 172],
  entrancePlate: "rgba(0,0,0,0.62)",

  you: [90, 190, 255],
  /** Ring around the location dot — has to contrast with the ground. */
  youCollar: [255, 255, 255, 235],
  viewshed: [255, 196, 90],
  /** Halo behind map text. Inverts with the basemap or the labels vanish. */
  textHalo: [0, 0, 0, 220],
  buildingSpecular: [40, 40, 44],

  /** Sky, seen when the camera tilts toward the horizon. */
  sky: "#000000",
  horizon: "#0a0f18",
  fog: "#05070c",

  markBlue: { core: [140, 185, 255], glow: [43, 108, 255] },
  markAmber: { core: [255, 208, 120], glow: [255, 176, 0] },
  markViolet: { core: [206, 160, 255], glow: [150, 80, 255] },
};

/**
 * GTA: the in-game map's bones — pale sand, bright water, heavy white roads —
 * with the loading-screen's treatment on top, meaning flat saturated blocks
 * and hard dark outlines rather than the subtle gradients above.
 *
 * The data colours are not the DedSec ones brightened. They are re-picked for
 * a light ground: teal on near-black reads beautifully and vanishes on sand,
 * so incidents go to a deep magenta that holds against it.
 */
const gta = {
  name: "GTA",
  light: true,

  ground: "#e6d9b8",
  water: "#3f9ad4",
  roadService: "#d8c9a4",
  roadMinor: "#f2ece0",
  roadMedium: "#ffffff",
  roadMajor: "#ffffff",
  roadHighway: "#ffd166",
  roadLabel: "#2b2b2b",
  roadLabelHalo: "#f6efdd",

  buildingLow: [206, 190, 158],
  buildingHigh: [246, 238, 220],
  buildingUnknown: [0.94, 0.9, 0.84],
  buildingTintRed: [188, 54, 48],
  buildingTintBlue: [48, 84, 176],

  park: [122, 176, 92, 220],
  parkLine: [22, 22, 20, 210],
  greenery: [150, 194, 120, 120],
  greeneryLine: [40, 44, 34, 90],
  parking: [198, 186, 160, 200],
  parkingLine: [22, 22, 20, 170],
  vacant: [206, 176, 128, 200],
  vacantLine: [22, 22, 20, 170],
  construction: [232, 176, 60, 210],
  constructionLine: [22, 22, 20, 200],
  tree: [86, 138, 66, 190],

  incident: [214, 22, 118],
  incidentDim: [150, 30, 96],

  cameraOsm: [92, 62, 132],
  cameraHalo: [70, 48, 110],
  cameraUser: [126, 84, 176],
  alpr: [200, 24, 24],
  alprPolice: [190, 40, 118],
  gunshot: [150, 70, 198],
  feed: [16, 132, 190],

  roadwork: [232, 138, 12],
  permit: [190, 130, 30],
  police: [200, 24, 24],
  event: [138, 44, 190],

  cotripIncident: [226, 88, 24],
  cotripCondition: [40, 110, 170],
  cotripClosed: [198, 16, 16],

  entranceDoor: 58,
  entranceGate: 74,
  entranceService: 92,
  entranceExit: 40,
  entranceInferred: [96, 92, 82],
  entrancePlate: "rgba(255,252,244,0.72)",

  you: [22, 110, 200],
  youCollar: [255, 252, 244, 245],
  viewshed: [226, 150, 20],
  textHalo: [252, 246, 232, 235],
  buildingSpecular: [255, 250, 236],

  sky: "#7fc4e8",
  horizon: "#e9d9b4",
  fog: "#f0e4c8",

  markBlue: { core: [30, 92, 200], glow: [70, 140, 240] },
  markAmber: { core: [224, 134, 16], glow: [255, 186, 40] },
  markViolet: { core: [128, 40, 180], glow: [176, 96, 226] },
};

export const THEMES = { dedsec, gta };

const DEFAULT = "dedsec";

let active = DEFAULT;
const listeners = new Set();

/** Restore the stored choice. Safe to call before the DOM is ready. */
export function initTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && THEMES[stored]) active = stored;
  } catch {
    // Private browsing and blocked storage both throw; the default is fine.
  }
  applyAttribute();
  return active;
}

function applyAttribute() {
  // `typeof`, not an optional chain: outside a browser `document` is not
  // merely null, it is undeclared, and reading a property off it throws a
  // ReferenceError. This module is imported by the test suite under Node.
  if (typeof document === "undefined") return;
  document.documentElement?.setAttribute("data-theme", active);
}

/** The active palette. Call this inside render(), not at module scope. */
export function palette() {
  return THEMES[active] ?? THEMES[DEFAULT];
}

export function themeName() {
  return active;
}

export function setTheme(name) {
  if (!THEMES[name] || name === active) return active;
  active = name;
  try {
    localStorage.setItem(STORAGE_KEY, name);
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
  applyAttribute();
  for (const listener of listeners) listener(active);
  return active;
}

/** Next theme in the list, for a single toggle button. */
export function cycleTheme() {
  const names = Object.keys(THEMES);
  return setTheme(names[(names.indexOf(active) + 1) % names.length]);
}

export function onThemeChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
